import { CurrencyPipe, formatDate } from "@angular/common";
import { Injectable, signal } from '@angular/core';
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { ColumnWithExportName, ExtendedColGroupDef } from "@models/grid/column-with-export-name";
import { FilterOption } from "@models/grid/grid-filter";
import { ClientSecurityTransaction } from "@models/security-transactions/client-security-transaction";
import { SecurityTransaction } from '@models/security-transactions/security-transaction';
import { SecurityTransactionType } from "@models/security-transactions/security-transaction-type.enum";
import { SecurityType } from "@models/shares/security-type";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import {
  AgShareActionsComponent
} from "@shared/components/grid/ag-cells/shares/ag-share-actions/ag-share-actions.component";
import {
  AgShareGroupHeaderComponent
} from "@shared/components/grid/ag-cells/shares/ag-share-group-header/ag-share-group-header.component";
import {
  AgShareHolderComponent
} from "@shared/components/grid/ag-cells/shares/ag-share-holder/ag-share-holder.component";
import {
  AgShareTransactionTypeComponent
} from "@shared/components/grid/ag-cells/shares/ag-share-transaction-type/ag-share-transaction-type.component";
import {
  AgShareWarningTooltipComponent
} from "@shared/components/grid/ag-cells/shares/ag-share-warning-tooltip/ag-share-warning-tooltip.component";
import {
  AgSharesNumberWithAlertComponent
} from "@shared/components/grid/ag-cells/shares/ag-shares-number-with-alert/ag-shares-number-with-alert.component";
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  GridApi,
  ICellRendererParams,
  ValueFormatterParams
} from "ag-grid-community";
import { ShareTypePipe } from "@shared/pipes/share-type.pipe";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";

@Injectable({
  providedIn: 'root'
})
export class SharesGridService {
  readonly autoSizeColumnList = ['numberIncrease', 'numberBalance', 'paidIncrease', 'paidBalance', 'unpaidIncrease', 'unpaidBalance'];

  isCompanyLoading = signal<boolean>(false);

  // callbacks
  deleteTransaction = signal<((transaction: SecurityTransaction) => void) | null>(null);
  editTransaction = signal<((transaction: SecurityTransaction) => void) | null>(null);

  // filters
  shareDateFilterOptions = signal<FilterOption[]>([]);
  shareTransactionTypeFilterOptions = signal<FilterOption[]>(convertEnumToFilterOption(SecurityTransactionType));
  shareholderNameFilterOptions = signal<FilterOption[]>([]);
  shareTypeIdentifierFilterOptions = signal<FilterOption[]>([]);
  shareTypeFilterOptions = signal<FilterOption[]>([]);

  // col defs
  colDefs = signal<(ColumnWithExportName | ExtendedColGroupDef)[]>([]);
  groupByShareholderColumnDefs = signal<(ColumnWithExportName | ColGroupDef)[]>([
    {
      field: '',
      width: 50,
      sortable: false,
      headerComponent: AgShareGroupHeaderComponent,
      cellRenderer: "agGroupCellRenderer",
      menuTabs: [],
    },
    {
      headerName: 'Shareholder',
      field: 'shareholders',
      autoHeight: true,
      flex: 3,
      cellRenderer: AgShareHolderComponent,
      menuTabs: ['generalMenuTab'],
    },
    ...this.getBeneficiallyOwnedColDefs(),
    ...this.getGroupByColDefsBase(),
  ]
    .map((colDef, initialSortIndex) => ({
      ...colDef,
      suppressHeaderMenuButton: true,
      initialSortIndex,
      sortIndex: initialSortIndex
    } as ColDef)));

  groupByShareholderPlusShareTypeColumnDefs = signal<(ColumnWithExportName | ColGroupDef)[]>([
    {
      field: '',
      width: 50,
      sortable: false,
      headerComponent: AgShareGroupHeaderComponent,
      cellRenderer: "agGroupCellRenderer",
      menuTabs: [],
    },
    {
      headerName: 'Shareholder',
      field: 'shareholders',
      autoHeight: true,
      flex: 3,
      cellRenderer: AgShareHolderComponent,
      menuTabs: ['generalMenuTab'],
    },
    ...this.getBeneficiallyOwnedColDefs(),
    {
      headerName: 'Share Type',
      field: 'shareType',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<unknown, string>) => ShareTypePipe.formatValue(params.value),
      menuTabs: ['generalMenuTab'],
    },
    ...this.getGroupByColDefsBase(),
  ]
    .map((colDef, initialSortIndex) => ({
      ...colDef,
      suppressHeaderMenuButton: true,
      initialSortIndex,
      sortIndex: initialSortIndex
    } as ColDef)));

  groupByShareholderPlusShareClassColumnDefs = signal<(ColumnWithExportName | ColGroupDef)[]>([
    {
      field: '',
      width: 50,
      sortable: false,
      headerComponent: AgShareGroupHeaderComponent,
      cellRenderer: "agGroupCellRenderer",
      menuTabs: [],
    },
    {
      headerName: 'Shareholder',
      field: 'shareholders',
      autoHeight: true,
      flex: 3,
      cellRenderer: AgShareHolderComponent,
      menuTabs: ['generalMenuTab'],
    },
    ...this.getBeneficiallyOwnedColDefs(),
    {
      headerName: 'Share Class',
      field: 'shareClass',
      flex: 1,
      menuTabs: ['generalMenuTab'],
    },
    ...this.getGroupByColDefsBase(),
  ]
    .map((colDef, initialSortIndex) => ({
      ...colDef,
      suppressHeaderMenuButton: true,
      initialSortIndex,
      sortIndex: initialSortIndex
    } as ColDef)));

  groupByShareClassColumnDefs = signal<(ColumnWithExportName | ColGroupDef)[]>([
    {
      field: '',
      width: 50,
      sortable: false,
      headerComponent: AgShareGroupHeaderComponent,
      cellRenderer: "agGroupCellRenderer",
      menuTabs: [],
    },
    {
      headerName: 'Share Class',
      field: 'shareClass',
      cellRenderer: AgShareWarningTooltipComponent,
      flex: 1,
      menuTabs: ['generalMenuTab'],
    },
    ...this.getGroupByColDefsBase(true),
  ]
    .map((colDef, initialSortIndex) => ({
      ...colDef,
      suppressHeaderMenuButton: true,
      initialSortIndex,
      sortIndex: initialSortIndex
    } as ColDef)));

  gridApi!: GridApi;

  constructor(private currencyPipe: CurrencyPipe) {
  }

  setSharesOptions(transactions: ClientSecurityTransaction[]): void {
    if (!transactions?.length) {
      this.colDefs.set([]);
      return;
    }

    const dateList = transactions.filter(share => share.transactionDate)?.map(share => share.transactionDate);
    this.shareDateFilterOptions.set([...new Set(dateList)].map(date => {
      return {
        label: formatDate(date ?? '', 'dd MMM yyyy', 'en'),
        value: date ?? '',
        active: false
      };
    }));

    const shareholderNameList = transactions
      .map((transaction) => transaction.holders.map((holder) => holder.name))
      .flat()
      .filter(Boolean);
    this.shareholderNameFilterOptions.set(uniqueArrayToFilterOptionHelper([...new Set(shareholderNameList)]));

    const shareTypeIdentifierList = transactions
      .filter(share => share.securityType.identifier)
      ?.map(share => share.securityType.identifier);
    const shareTypeIdentifierFilterOptions = uniqueArrayToFilterOptionHelper([...new Set(shareTypeIdentifierList)]).map(item => {
      item.label = ShareTypePipe.formatValue(item.label, false);
      return item;
    });
    this.shareTypeIdentifierFilterOptions.set(shareTypeIdentifierFilterOptions);

    const shareTypeList = transactions
      .filter(share => share.securityType.class)
      ?.map(share => share.securityType.class);
    this.shareTypeFilterOptions.set(uniqueArrayToFilterOptionHelper([...new Set(shareTypeList)]));
    this.setGridConfig();
  }

  setGridConfig(): void {
    this.colDefs.set([
        {
          headerName: 'Date',
          field: 'transactionDate',
          width: 80,
          flex: 0.5,
          suppressSizeToFit: true,
          editable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            filterOptions: this.shareDateFilterOptions(),
          },
          cellDataType: CellDataType.DATE_STRING,
          cellRenderer: AgDateCellComponent,
          menuTabs: ['generalMenuTab'],
        },
        {
          headerName: 'Type',
          field: 'transactionType',
          width: 80,
          flex: 0.5,
          suppressSizeToFit: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            filterOptions: this.shareTransactionTypeFilterOptions(),
          },
          cellRenderer: AgShareTransactionTypeComponent,
          menuTabs: ['generalMenuTab'],
        },
        {
          headerName: 'Shareholder',
          field: 'holders',
          autoHeight: true,
          width: 80,
          flex: 0.5,
          suppressSizeToFit: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            filterOptions: this.shareholderNameFilterOptions(),
          },
          filterValueGetter: (params: {
            data: { holder: { name: string } }
          }) => params?.data?.holder ? params.data.holder.name : '',
          cellRenderer: AgShareHolderComponent,
          menuTabs: ['generalMenuTab'],
        },
        ...this.getBeneficiallyOwnedColDefs(80),
        {
          headerName: 'Share Type',
          field: 'securityType.identifier',
          width: 80,
          flex: 0.5,
          suppressSizeToFit: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            filterOptions: this.shareTypeIdentifierFilterOptions(),
          },
          cellRenderer: (params: ICellRendererParams<unknown, string>) => ShareTypePipe.formatValue(params.value),
          menuTabs: ['generalMenuTab'],
        },
        {
          headerName: "Number of Shares",
          groupId: "numberOfSharesGroup",
          children: [
            {
              headerName: 'Inc',
              exportColumnName: 'Number of Share Increase',
              field: 'numberIncrease',
              width: 80,
              flex: 0.3,
              hide: false,
              valueFormatter: (params: ValueFormatterParams<unknown, string>) => this.currencyPipe.transform(params.value ?? '', '', '', '1.0'),
              menuTabs: [],
            },
            {
              headerName: 'Bal',
              exportColumnName: 'Number of Shares Balance',
              field: 'numberBalance',
              width: 80,
              flex: 0.3,
              hide: false,
              valueFormatter: (params: ValueFormatterParams<unknown, string>) => this.currencyPipe.transform(params.value ?? '', '', '', '1.0'),
              menuTabs: [],
            },
          ],
          hide: false,
        },
        {
          headerName: "Paid Amount",
          groupId: "paidAmountGroup",
          children: [
            {
              headerName: 'Inc',
              exportColumnName: 'Paid Increase',
              field: 'paidIncrease',
              width: 80,
              flex: 0.3,
              hide: false,
              valueFormatter: (params: ValueFormatterParams<unknown, number>) => SecurityType.getMoneyLabel(params.value, this.currencyPipe),
              menuTabs: [],
            },
            {
              headerName: 'Bal',
              exportColumnName: 'Paid Balance',
              field: 'paidBalance',
              width: 80,
              flex: 0.3,
              hide: false,
              valueFormatter: (params: ValueFormatterParams<unknown, number>) => SecurityType.getMoneyLabel(params.value, this.currencyPipe),
              menuTabs: [],
            },
          ],
          hide: false,
        },
        {
          headerName: "Unpaid Amount",
          groupId: "unpaidAmountGroup",
          children: [
            {
              headerName: 'Inc',
              exportColumnName: 'Unpaid Increase',
              field: 'unpaidIncrease',
              width: 80,
              flex: 0.3,
              hide: false,
              valueFormatter: (params: ValueFormatterParams<unknown, number>) => SecurityType.getMoneyLabel(params.value, this.currencyPipe),
              menuTabs: [],
            },
            {
              headerName: 'Bal',
              exportColumnName: 'Unpaid of Balance',
              field: 'unpaidBalance',
              width: 80,
              flex: 0.3,
              hide: false,
              valueFormatter: (params: ValueFormatterParams<unknown, number>) => SecurityType.getMoneyLabel(params.value, this.currencyPipe),
              menuTabs: [],
            },
          ],
          hide: false,
        },
        {
          headerName: 'Certificate Number',
          field: 'shareCertificateNumber',
          width: 80,
          flex: 0.3,
          suppressSizeToFit: true,
          menuTabs: ['generalMenuTab'],
        },
        {
          headerName: 'Share Class',
          field: 'securityType.class',
          width: 80,
          flex: 0.5,
          suppressSizeToFit: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            filterOptions: this.shareTypeFilterOptions(),
          },
          hide: true,
          menuTabs: ['generalMenuTab'],
        },
        {
          headerName: 'Counterparty',
          field: 'counterparty',
          width: 80,
          flex: 0.5,
          suppressSizeToFit: true,
          hide: true,
          menuTabs: ['generalMenuTab'],
        },
        {
          field: '',
          width: 120,
          suppressSizeToFit: true,
          sortable: false,
          cellClass: 'actions-button-cell',
          cellRenderer: AgShareActionsComponent,
          cellRendererParams: {
            // annotation: this.annotation.bind(this),
            // downloadCertificate: this.downloadCertificate.bind(this),
            edit: this.editTransaction(),
            delete: this.deleteTransaction()
          },
        },
      ]
        .map((colDef, initialSortIndex) => ({
          ...colDef,
          suppressHeaderMenuButton: true,
          initialSortIndex,
          sortIndex: initialSortIndex
        } as ColDef))
    );
  }

  getBeneficiallyOwnedColDefs(width = 200): ColDef[] {
    return [
      {
        headerName: 'Beneficially Owned',
        field: 'isBeneficialOwnerLabel',
        cellClass: 'beneficially-owned-tag',
        minWidth: 75,
        width: 100,
        maxWidth: 100,
        wrapHeaderText: true,
        suppressSizeToFit: true,
        cellRenderer: (params: ICellRendererParams<ClientSecurityTransaction, string>) => params.value,
        cellClassRules: {
          'green': (params: CellClassParams<ClientSecurityTransaction, string>) => params.value == 'Yes',
          'purple': (params: CellClassParams<ClientSecurityTransaction, string>) => params.value == 'No',
        },
      },
      {
        headerName: 'Beneficial Owner',
        field: 'beneficialOwnerNames',
        minWidth: 80,
        width,
        flex: 0.5,
        valueFormatter: (params: ValueFormatterParams<ClientSecurityTransaction, string>) => params.data?.beneficialOwnerNames || '-',
      },
    ];
  }

  getGroupByColDefsBase(showAlertPaidUnpaid = false): (ColumnWithExportName | ColGroupDef)[] {
    return [
      {
        headerName: 'Number of Shares',
        field: "numberOfShares",
        cellRenderer: AgSharesNumberWithAlertComponent,
        cellRendererParams: {
          showAlert: true,
          valueKey: 'numberOfShares',
          alertValueKey: 'numberOfSharesAsic',
          alertPrefix: '(ASIC) '
        },
        flex: 1,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Paid Amount',
        field: "paidAmount",
        cellRenderer: AgSharesNumberWithAlertComponent,
        cellRendererParams: {
          showAlert: showAlertPaidUnpaid,
          valueKey: 'paidAmount',
          alertValueKey: 'paidAmountAsic',
          alertPrefix: '(ASIC) ',
          applyCurrency: true
        },
        flex: 1,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Unpaid Amount',
        field: "unpaidAmount",
        cellRenderer: AgSharesNumberWithAlertComponent,
        cellRendererParams: {
          showAlert: showAlertPaidUnpaid,
          valueKey: 'unpaidAmount',
          alertValueKey: 'unpaidAmountAsic',
          alertPrefix: '(ASIC) ',
          applyCurrency: true
        },
        flex: 1,
        menuTabs: ['generalMenuTab'],
      },
    ];
  }
}
